import React from 'react';
import styled from 'styled-components';
import { CardRow } from '../../../../../generic/ui/CardRow';
import { Mono } from '../../../../../generic/ui/Mono';
import BarcodeIcon from '../../../../../generic/icons/IconLib/BarcodeIcon';
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon';

export interface EntryRowProps {
    entryNum: number | string;
    reference?: number | string;
    primary: string;
    date?: string;
    time?: string;
    trailing?: React.ReactNode;
    isLast?: boolean;
    onClick?: () => void;
    className?: string;
}

const EntryRow: React.FC<EntryRowProps> = ({
    entryNum,
    reference,
    primary,
    date,
    time,
    trailing,
    isLast = false,
    onClick,
    className,
}) => {
    const interactive = typeof onClick === 'function';
    const handleKeyDown = interactive
        ? (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.();
              }
          }
        : undefined;

    return (
        <CardRow
            className={className}
            $columns='6rem minmax(0, 1fr) auto auto'
            $mobileColumns='auto minmax(0, 1fr) auto auto'
            $isLast={isLast}
            $interactive={interactive}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
        >
            <CardRow.Cell>
                <CardRow.Stack $gap='0.125rem'>
                    <CardRow.Line $gap='0.375rem'>
                        <IdIcon>
                            <BarcodeIcon color='currentColor' size={16} />
                        </IdIcon>
                        <Id>{entryNum}</Id>
                    </CardRow.Line>
                    {reference != null && reference !== '' && <Reference>{reference}</Reference>}
                </CardRow.Stack>
            </CardRow.Cell>

            <CardRow.Cell>
                <CardRow.Line $gap='0.5rem'>
                    <NameIcon>
                        <UserIcon color='currentColor' size={16} />
                    </NameIcon>
                    <PrimaryName>{primary}</PrimaryName>
                </CardRow.Line>
            </CardRow.Cell>

            <CardRow.Cell $align='center'>
                <CardRow.Stack $gap='0.125rem' $align='center'>
                    {date && <When>{date}</When>}
                    {time && <TimeText>{time}</TimeText>}
                </CardRow.Stack>
            </CardRow.Cell>

            <CardRow.Cell $align='center'>{trailing}</CardRow.Cell>
        </CardRow>
    );
};

export default EntryRow;

const IdIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink2};
    flex-shrink: 0;
`;

const NameIcon = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const Reference = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.ink4};
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const PrimaryName = styled.span`
    font-size: ${({ theme }) => theme.text.lg};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
    white-space: nowrap;
    font-family: ${({ theme }) => theme.fonts.sans};
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const When = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink2};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const TimeText = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;
